<script setup lang="ts">
import { onErrorCaptured, ref } from 'vue'
import { RouterLink } from 'vue-router'

import BaseButton from '@/components/base/BaseButton.vue'
import BaseIcon from '@/components/base/BaseIcon.vue'
import { reportError } from '@/utils/report-error.util.js'

/**
 * Catches a render or lifecycle error in the routed screen and shows a screen the operator
 * can act on, instead of the blank pane Vue leaves behind.
 *
 * Scoped to the routed view — the shell, the toasts and the navigation stay alive, so the
 * operator can go somewhere else. `App.vue` keys this on the route, so a navigation is a
 * reset: the next screen gets a clean boundary rather than inheriting this one's failure.
 * "Try again" re-renders the same screen in place for the transient case.
 */
const error = ref<Error | null>(null)
const attempt = ref(0)

onErrorCaptured((thrown) => {
  error.value = thrown instanceof Error ? thrown : new Error(String(thrown))
  // Reported, not swallowed: the console is where a developer reproducing a ticket looks.
  reportError('ErrorBoundary', thrown)

  return false
})

function retry(): void {
  error.value = null
  attempt.value += 1
}
</script>

<template>
  <div
    v-if="error !== null"
    class="mx-auto flex max-w-lg flex-col items-start gap-4 rounded-lg border border-danger bg-danger-tint p-6"
    role="alert"
  >
    <div class="flex items-center gap-2 text-danger-on-tint">
      <BaseIcon
        name="warning-circle"
        :size="20"
        weight="fill"
      />
      <h2 class="text-title">This screen hit an error</h2>
    </div>
    <p class="text-body text-ink">
      The rest of the panel still works. If it happens again, the message below is what to quote.
    </p>
    <pre class="max-w-full overflow-x-auto rounded-md bg-raised px-3 py-2 text-mono text-ink-secondary">{{ error.message }}</pre>
    <div class="flex flex-wrap gap-2">
      <BaseButton @click="retry()">Try again</BaseButton>
      <RouterLink :to="{ name: 'overview' }">
        <BaseButton variant="secondary">Go to the dashboard</BaseButton>
      </RouterLink>
    </div>
  </div>
  <slot
    v-else
    :key="attempt"
  />
</template>
